import { Component, Input, ChangeDetectionStrategy } from '@angular/core';

import { RouterLink } from '@angular/router';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { IconType } from '../../../../../shared/services/icon.service';
import { UserMembership } from '../../../../models/profile.interface';
import { UserOrganization } from '../../../../../shared/services/user-organization.service';
import { to, Cmd } from '../../../../../core/constants/routes';

@Component({
  selector: 'app-clubs-list',
  standalone: true,
  imports: [RouterLink, IconComponent],
  templateUrl: './clubs-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './clubs-list.component.css',
})
export class ClubsListComponent {
  @Input() organizations: UserOrganization[] = [];
  @Input() memberships: UserMembership[] = [];
  @Input() loading = false;

  readonly IconType = IconType;

  getOrganizationMemberships(organizationId: string): UserMembership[] {
    return this.memberships.filter(
      (m) => m.organizationId === organizationId
    );
  }

  getActiveMembershipsCount(organizationId: string): number {
    return this.getOrganizationMemberships(organizationId).filter(
      (m) => this.isMembershipActive(m)
    ).length;
  }

  /**
   * Check if a membership is currently active.
   *
   * Date comparison is timezone-agnostic: JavaScript's Date.getTime()
   * returns UTC milliseconds, so comparing ISO 8601 strings (with Z suffix)
   * parsed via new Date() correctly compares UTC instants regardless of
   * browser timezone.
   *
   * See docs/TIMEZONE_HANDLING.md for the timezone architecture.
   */
  isMembershipActive(membership: UserMembership): boolean {
    const now = new Date();
    const validFrom = new Date(membership.validFrom);
    const validUntil = membership.validUntil
      ? new Date(membership.validUntil)
      : null;

    if (validFrom > now) return false;
    if (validUntil && validUntil < now) return false;
    return true;
  }

  getRoleLabel(role: string): string {
    switch (role?.toUpperCase()) {
      case 'ADMIN':
        return 'Administrator';
      case 'USER':
        return 'Nutzer';
      default:
        return role || 'Mitglied';
    }
  }

  getRoleBadgeClass(role: string): string {
    const base = 'role-badge';
    switch (role?.toUpperCase()) {
      case 'ADMIN':
        return `${base} role-admin`;
      default:
        return `${base} role-user`;
    }
  }

  formatDate(date: Date | string): string {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  getMembershipsSummary(organizationId: string): string {
    const memberships = this.getOrganizationMemberships(organizationId);
    const active = memberships.filter((m) => this.isMembershipActive(m));

    if (active.length === 0) {
      return 'Keine aktiven Mitgliedschaften';
    }

    if (active.length === 1) {
      return active[0].membershipType.name;
    }

    return `${active.length} aktive Mitgliedschaften`;
  }

  getClubDetailLink(organizationId: string): Cmd {
    return to.clubDetail(organizationId);
  }
}
